import { useMemo, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import {
  Typography,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  useMediaQuery,
  IconButton,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import KindOfFlightCard from '../FlightCard/KindOfFlightCard';
import FlightScaleRow from '../FlightCard/FlightScaleRow';
import IndicateScaleCard from '../FlightCard/IndicateScaleCard';
import { useColors } from 'legacyfrontend/hooks/useColors';

const ColorButton = styled(Button)({
  color: '#3A3A3A',
  backgroundColor: 'transparent',
  '&:hover': {
    color: '#444',
  },
});

const DialogStyles = {
  title: {
    display: 'flex',
    flexDirection: 'row',
    flex: '1 auto auto',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 5px 16px 2rem',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    padding: '0 0 2rem 0',
    maxHeight: { sm: 'inherit', lg: 600 },
    overflow: { sm: 'inherit' },
    alignSelf: 'center',
    width: { xs: '100%' },
  },
};

const FlightSingleDetailDialog = ({ flight, kind, originId, destinationId, scales, duration }) => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { background } = useColors();

  const validScales = useMemo(() => scales.filter((scale) => !scale.isNotScale), [scales]);

  const handleToggleDialog = () => setOpen((prev) => !prev);

  const adaptSegmentToFlightFormat = (segment) => ({
    arrival: {
      city: segment.destination_city_name,
      name: segment.destination_short_name,
      country: '', // No disponible en el nuevo formato
      type: segment.destination_type,
    },
    departure: {
      city: segment.origin_city_name,
      name: segment.origin_short_name,
      country: '', // No disponible en el nuevo formato
      type: segment.origin_type,
    },
    arrivalDate: segment.arrival_date_time,
    departureDate: segment.departure_date_time,
    company: segment.company_name,
    originDestinationReferences: `${segment.origin_iata}-${segment.destination_iata}`,
  });

  return (
    <>
      {isMobile ? (
        <IconButton
          onClick={handleToggleDialog}
          size='small'
          sx={{
            color: background,
          }}
        >
          <InfoIcon sx={{ color: background }} />
        </IconButton>
      ) : (
        <ColorButton color='info' onClick={handleToggleDialog} size='small' variant='text'>
          Más Detalles
        </ColorButton>
      )}

      <Dialog
        PaperProps={{
          sx: {
            borderRadius: '15px',
          },
        }}
        aria-labelledby='flight-detail-dialog'
        disableScrollLock={false}
        fullScreen={fullScreen}
        fullWidth
        maxWidth='md'
        onClose={handleToggleDialog}
        open={open}
        slotProps={{
          backdrop: {
            style: { backgroundColor: 'rgba(0, 0, 0, 0.5)' },
          },
        }}
      >
        <DialogTitle sx={DialogStyles.title}>
          <Typography fontSize={24} fontWeight={700}>
            Detalle de vuelo
          </Typography>
          <Button onClick={handleToggleDialog}>
            <CloseIcon style={{ fill: '#000' }} />
          </Button>
        </DialogTitle>

        <DialogContent sx={DialogStyles.content}>
          <KindOfFlightCard
            destinationId={destinationId}
            duration={duration}
            isVueloDeIda={kind === 'ida'}
            isVueloDeVuelta={kind === 'vuelta'}
            originId={originId}
          />

          {flight.map((segment, index) => {
            const adaptedSegment = adaptSegmentToFlightFormat(segment);
            return (
              <div key={`flight-segment-${index}`}>
                <FlightScaleRow {...adaptedSegment} />
                {index < flight.length - 1 && validScales[index] && (
                  <IndicateScaleCard info={validScales[index]} key={index} />
                )}
              </div>
            );
          })}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FlightSingleDetailDialog;
